import React, { useContext, useEffect, useMemo, useState } from "react";
import { approveCompanyApplication, fetchPendingCompanyApplications } from "../api/companyApplications";
import { AuthContext } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";

export function PartnerApplications() {
  const { isPlatformAdmin } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [status, setStatus] = useState(null);
  const [trustedSelections, setTrustedSelections] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isPlatformAdmin) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchPendingCompanyApplications()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const nextApplications = response.data?.applications || [];
        setApplications(nextApplications);
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({ type: "error", text: t("partnerAdmin.loadError") });
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, t]);

  const orderedApplications = useMemo(
    () => [...applications].sort((a, b) => {
      const aDate = Date.parse(a.moved_to_review_at || a.submitted_at || 0);
      const bDate = Date.parse(b.moved_to_review_at || b.submitted_at || 0);
      return aDate - bDate;
    }),
    [applications]
  );

  const handleTrustedChange = (applicationId) => (event) => {
    const checked = event.target.checked;
    setTrustedSelections((current) => ({ ...current, [applicationId]: checked }));
  };

  const handleApprove = async (applicationId) => {
    setLoadingId(applicationId);
    setStatus(null);
    try {
      await approveCompanyApplication(applicationId, {
        trusted_for_certification: Boolean(trustedSelections[applicationId]),
      });
      setApplications((current) => current.filter((application) => application.application_id !== applicationId));
      setStatus({ type: "success", text: t("partnerAdmin.approveSuccess") });
    } catch (error) {
      const messageKey = error.response?.data?.message_key;
      const message = error.response?.data?.message;
      setStatus({
        type: "error",
        text: t(messageKey || "partnerAdmin.approveError", {
          defaultValue: message || t("partnerAdmin.approveError"),
        }),
      });
    } finally {
      setLoadingId(null);
    }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="admin-users-screen">
        <div className="admin-users-card">
          <div className="admin-users-card__header">
            <h2 className="admin-users-card__title">{t("partnerAdmin.title")}</h2>
            <p className="admin-users-card__subtitle">{t("partnerAdmin.notAuthorized")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-screen">
      <div className="admin-users-card admin-users-card--wide">
        <div className="admin-users-card__header">
          <h2 className="admin-users-card__title">{t("partnerAdmin.title")}</h2>
          <p className="admin-users-card__subtitle">{t("partnerAdmin.subtitle")}</p>
        </div>

        {status && (
          <p className={`admin-users-status admin-users-status--${status.type}`} role="status">
            {status.text}
          </p>
        )}

        {isLoading ? (
          <p className="admin-users-list__empty">{t("common.loading")}</p>
        ) : orderedApplications.length === 0 ? (
          <p className="admin-users-list__empty">{t("partnerAdmin.empty")}</p>
        ) : (
          <div className="partner-admin-list">
            {orderedApplications.map((application) => (
              <section key={application.application_id} className="partner-admin-card">
                <div className="partner-admin-card__header">
                  <div>
                    <h3>{application.display_name}</h3>
                    <p>{application.legal_name}</p>
                  </div>
                  <span className="partner-admin-card__badge">
                    {t(`partnerApply.businessCategory.options.${application.business_category}`)}
                  </span>
                </div>

                <dl className="partner-admin-card__meta">
                  <div>
                    <dt>{t("partnerAdmin.meta.numisId")}</dt>
                    <dd>{application.user?.numistrail_id || t("common.notAvailable")}</dd>
                  </div>
                  <div>
                    <dt>{t("partnerAdmin.meta.email")}</dt>
                    <dd>{application.user?.email || t("common.notAvailable")}</dd>
                  </div>
                  <div>
                    <dt>{t("partnerAdmin.meta.website")}</dt>
                    <dd>{application.website}</dd>
                  </div>
                  <div>
                    <dt>{t("partnerAdmin.meta.country")}</dt>
                    <dd>{application.country}</dd>
                  </div>
                  {application.phone ? (
                    <div>
                      <dt>{t("partnerAdmin.meta.phone")}</dt>
                      <dd>{application.phone}</dd>
                    </div>
                  ) : null}
                  {application.address ? (
                    <div className="partner-admin-card__meta--full">
                      <dt>{t("partnerAdmin.meta.address")}</dt>
                      <dd>{application.address}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>{t("partnerAdmin.meta.submitted")}</dt>
                    <dd>{formatDate(application.moved_to_review_at || application.submitted_at, i18n.language)}</dd>
                  </div>
                </dl>

                <label className="cert-checkbox partner-admin-card__trusted-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(trustedSelections[application.application_id])}
                    onChange={handleTrustedChange(application.application_id)}
                    disabled={loadingId === application.application_id}
                  />
                  <span>{t("partnerAdmin.trustedToggle")}</span>
                </label>

                <button
                  type="button"
                  className="cert-button"
                  onClick={() => handleApprove(application.application_id)}
                  disabled={loadingId === application.application_id}
                >
                  {loadingId === application.application_id
                    ? t("partnerAdmin.approving")
                    : t("partnerAdmin.approve")}
                </button>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(rawValue, locale) {
  if (!rawValue) {
    return "";
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat(locale || "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}
